'use client'

import { useEffect, useMemo, useState } from 'react'
import nextDynamic from 'next/dynamic'

/* Real volunteer map — reuses the app's existing live map (DirectoryLiveMap).
   Plots REAL masjids on the network (the records volunteers help verify/enrich);
   chips are REAL aggregations from /api/stats. No hardcoded counts or fake pins. */

const DirectoryLiveMap = nextDynamic(() => import('../directory/DirectoryLiveMap'), {
  ssr: false,
  loading: () => <MapShell label="Loading map…" />,
})

function MapShell({ label }: { label: string }) {
  return (
    <div style={{ height: 380, borderRadius: 18, background: 'linear-gradient(160deg,#101A38,#0B132B)', display: 'grid', placeItems: 'center', color: '#8A94A8', fontSize: 12, letterSpacing: '.04em', textAlign: 'center', padding: '0 24px' }}>
      {label}
    </div>
  )
}

type Masjid = { slug: string; nameEnglish: string; city?: string | null; latitude?: unknown; longitude?: unknown; verified?: boolean | null }
type Stats = { mapped?: number; verified?: number; cities?: number }

export default function VolunteerMap() {
  const [masjids, setMasjids] = useState<Masjid[] | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    fetch('/api/masjids?limit=400')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return
        if (d && Array.isArray(d.masjids)) setMasjids(d.masjids)
        else setFailed(true)
      })
      .catch(() => { if (alive) setFailed(true) })
    fetch('/api/stats').then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive && d) setStats(d) }).catch(() => {})
    return () => { alive = false }
  }, [])

  const records = useMemo(() =>
    (masjids || [])
      .map((m) => ({
        slug: m.slug,
        name: m.nameEnglish,
        city: m.city,
        lat: Number(m.latitude),
        lng: Number(m.longitude),
        verified: m.verified,
        href: `/masjids/${m.slug}`,
        /* CTA reflects the real volunteer action: an UNVERIFIED masjid (verified===false)
           is exactly what "needs volunteers" — so the marker asks them to verify it.
           Both link to /masjids/{slug}, where the volunteer acts. No fabricated state:
           the flag comes straight from the record. */
        ctaLabel: m.verified ? 'View masjid →' : 'Verify this masjid →',
        meta: m.verified ? 'Verified' : 'Needs review',
      }))
      .filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lng) && r.lat !== 0 && r.lng !== 0),
  [masjids])

  if (masjids === null && !failed) return <MapShell label="Loading map…" />
  if (failed) return <MapShell label="Map data is temporarily unavailable." />

  const plotted = records.length
  const toVerify = stats?.mapped != null && stats?.verified != null ? stats.mapped - stats.verified : null
  const chips: Array<{ k: string; v: string }> = [{ k: 'on map', v: String(plotted) }]
  if (stats?.verified != null) chips.push({ k: 'verified', v: String(stats.verified) })
  if (stats?.cities != null) chips.push({ k: 'cities', v: String(stats.cities) })
  if (toVerify != null) chips.push({ k: 'to verify', v: String(toVerify) })

  return (
    <div>
      {plotted > 0 ? (
        <DirectoryLiveMap records={records} height={380} headerLabel="Masjids on the network" headerChips={chips} />
      ) : (
        <MapShell label="No mapped masjids to show yet." />
      )}
      <p style={{ fontSize: 12, color: '#8a887f', marginTop: 10, lineHeight: 1.5 }}>
        Every marker is a real listing — newly imported masjids need local volunteers to verify details and add photos.
      </p>
    </div>
  )
}
